// src/pages/CustomFields/CustomFields.jsx
import React, { useEffect, useState, lazy, Suspense } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Helmet from "../../components/Helmet/Helmet";
import { fetchCategories } from "../../components/Header/Data";
import Footer from "../../components/Footer/Footer";
import { AnimatePresence, motion } from "framer-motion";
import "./CustomFields.css";
import {
  AddCustomFields,
  GetCustomFields,
  deleteField,
  updateField,
} from "../../redux/slices/CustomFieldsSlice";
import { toast } from "react-toastify";

const QuestionsForm = lazy(() => import("./QuestionsForm"));

const CustomFields = () => {
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedQuestion, setExpandedQuestion] = useState(null);
  const [editingQuestionId, setEditingQuestionId] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const islogin = useSelector((state) => state.user.isLogin);
  const userinfo = useSelector((state) => state.user.userInfo);
  const user = islogin ? JSON.parse(userinfo) : null;

  const {
    catCustomFields,
    loadingCustomFields,
    errorCustomFields,
    addingField,
    updatingField,
    errorAddingField,
    errorUpdatingField,
  } = useSelector((state) => state.customFields);

  // Fetch categories + initial load
  useEffect(() => {
    const fetchCat = async () => {
      try {
        setLoading(true);
        const cat = await fetchCategories();
        setCategories(cat);

        const urlParams = new URLSearchParams(location.search);
        const categoryId = urlParams.get("category");
        if (categoryId) {
          const sel = cat.find((c) => c.Id === parseInt(categoryId));
          if (sel) {
            setSelectedCategory(sel);
            dispatch(GetCustomFields(sel.Id));
          }
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchCat();
  }, [location.search, dispatch]);

  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
    navigate(`?category=${category.Id}`);
    dispatch(GetCustomFields(category.Id));
  };

  const handleBackClick = () => {
    setSelectedCategory(null);
    navigate("");
  };

  const toggleQuestion = (id) => {
    setExpandedQuestion((prev) => (prev === id ? null : id));
  };

  const startEdit = (id) => {
    setEditingQuestionId(id);
    setExpandedQuestion(id);
  };

  const cancelEdit = () => {
    setEditingQuestionId(null);
  };

  // ───── ADD QUESTION ─────
  const handleAddQuestion = async (newQuestionData) => {
    const tempId = `temp-${Date.now()}`;
    await dispatch(
      AddCustomFields({
        ...newQuestionData,
        tempId,
      })
    );
    if (!errorAddingField) {
      toast.success("Question added successfully.");
    }
  };

  // ───── UPDATE QUESTION ─────
  const handleUpdateQuestion = async (questionId, updatedData) => {
    await dispatch(
      updateField({
        id: questionId,
        data: updatedData,
      })
    );
    if (!errorUpdatingField) {
      toast.success("Question updated successfully.");
      setEditingQuestionId(null);
    }
  };

  // ───── DELETE QUESTION ─────
  const handleDeleteQuestion = (questionId) => {
    dispatch(
      deleteField({
        id: questionId,
        params: {
          Id: questionId,
          DeletedBy: user?.UserID,
        },
      })
    );
    toast.success("Question deleted successfully.");
  };

  // Skeletons
  const CategorySkeleton = () => (
    <div className="category-list">
      {[...Array(12)].map((_, i) => (
        <div
          key={i}
          className="cstm-fld-skeleton cstm-fld-skeleton-category"
        ></div>
      ))}
    </div>
  );

  const QuestionsSkeleton = () => (
    <div className="p-8 rounded-xl shadow-md">
      <div className="cstm-fld-skeleton cstm-fld-skeleton-title mb-4"></div>
      <div className="cstm-fld-skeleton cstm-fld-skeleton-form"></div>
      {[...Array(3)].map((_, i) => (
        <div
          key={i}
          className="cstm-fld-skeleton cstm-fld-skeleton-question"
        ></div>
      ))}
    </div>
  );

  const valueTypeMap = (value) => {
    // ... (same as before)
    let type = "";
    let custom = null;
    try {
      if (value.customValue) custom = JSON.parse(value.customValue);
    } catch (_) {}

    switch (value.valueType) {
      case 0:
        type = "Checkbox";
        break;
      case 1:
        type = "Dropdown";
        break;
      case 2:
        type = "Text Box";
        break;
      case 3:
        type = "Number";
        break;
      case 4:
        type = "Date";
        break;
      case 5:
        type = "Time";
        break;
      default:
        type = "Custom ქართული";
    }

    return (
      <>
        <div className="value-type">{type}</div>
        {value.Description && (
          <div className="description-text">
            Description: {value.Description}
          </div>
        )}
        {Array.isArray(custom) && custom.length > 0 && (
          <div className="custom-value">
            Options:{" "}
            {custom.map((opt, i) => (
              <span key={i} className="custom-item">
                {opt}
                {i < custom.length - 1 ? ", " : ""}
              </span>
            ))}
          </div>
        )}
      </>
    );
  };

  return (
    <Helmet title="Vendor Questions">
      <div className="min-h-screen bg-gradient-to-b from-gray-100 to-gray-200 flex flex-col font-sans">
        <main className="flex-grow max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
          {loading || loadingCustomFields ? (
            <div className="animate-fade-in">
              {!selectedCategory ? (
                <>
                  <div className="description">
                    <div className="cstm-fld-skeleton cstm-fld-skeleton-description"></div>
                  </div>
                  <CategorySkeleton />
                </>
              ) : (
                <div>
                  <div className="cstm-fld-skeleton cstm-fld-skeleton-button mb-4"></div>
                  <QuestionsSkeleton />
                </div>
              )}
            </div>
          ) : (
            <div className="animate-fade-in">
              {/* Category List */}
              {!selectedCategory ? (
                <>
                  <div className="description">
                    Choose a category below to view and manage related vendor
                    questions.
                  </div>
                  <div className="category-list">
                    <AnimatePresence>
                      {categories.map((item) => (
                        <motion.div
                          key={item.Id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 20 }}
                          transition={{ duration: 0.4, ease: "easeOut" }}
                          className={`category-item ${
                            item.Id === selectedCategory?.Id ? "selected" : ""
                          }`}
                          onClick={() => handleCategoryClick(item)}
                        >
                          <h3>{item.Name}</h3>
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </div>
                </>
              ) : (
                /* ── SELECTED CATEGORY VIEW ── */
                <div>
                  <motion.button
                    className="back-button"
                    onClick={handleBackClick}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    ← Back to Categories
                  </motion.button>

                  <div className="p-8 rounded-xl shadow-md">
                    <h2 className="text-2xl font-bold mb-4">
                      {selectedCategory.Name}
                    </h2>

                    <Suspense
                      fallback={
                        <div className="cstm-fld-skeleton cstm-fld-skeleton-form"></div>
                      }
                    >
                      <QuestionsForm
                        onAddQuestion={handleAddQuestion}
                        selectedCategory={selectedCategory}
                        disabled={addingField}
                      />
                    </Suspense>

                    {errorAddingField && (
                      <p className="text-red-500 mb-4">
                        {errorAddingField?.message || "Failed to add question."}
                      </p>
                    )}

                    <AnimatePresence>
                      {catCustomFields
                        .filter((q) => q.categoryId === selectedCategory.Id)
                        .map((question) => (
                          <motion.div
                            key={question.id}
                            className="question-item"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                          >
                            <div
                              className="question-header"
                              onClick={() => toggleQuestion(question.id)}
                            >
                              <div className="question-content">
                                <p className="question-text">
                                  {question.Question}
                                  {question.isMandatory == 1 && (
                                    <span className="isMandatory">
                                      {" "}
                                      (Mandatory)
                                    </span>
                                  )}
                                </p>
                                <span className="question-type">
                                  {valueTypeMap(question)}
                                </span>
                              </div>
                              <motion.span
                                className="toggle-icon"
                                animate={{
                                  rotate:
                                    expandedQuestion === question.id ? 45 : 0,
                                }}
                              >
                                {expandedQuestion === question.id ? "×" : "+"}
                              </motion.span>
                            </div>

                            {expandedQuestion === question.id && (
                              <motion.div
                                className="question-details"
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                              >
                                {editingQuestionId === question.id ? (
                                  <Suspense fallback={<div>Loading...</div>}>
                                    <QuestionsForm
                                      selectedCategory={selectedCategory}
                                      initialData={question}
                                      isEdit={true}
                                      onSave={(data) =>
                                        handleUpdateQuestion(question.id, data)
                                      }
                                      onCancel={cancelEdit}
                                    />
                                  </Suspense>
                                ) : (
                                  <>
                                    {/* <p className="description-text">
                                      Description: {question.Description}
                                    </p>
                                    {(question.valueType === 0 ||
                                      question.valueType === 1) &&
                                      question.customValue && (
                                        <p className="options-text">
                                          Options:{" "}
                                          {JSON.parse(
                                            question.customValue
                                          ).join(", ")}
                                        </p>
                                      )} */}
                                    <div className="flex gap-2">
                                      <motion.button
                                        className="edit-button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          startEdit(question.id);
                                        }}
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                      >
                                        Edit
                                      </motion.button>
                                      <motion.button
                                        className="delete-button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDeleteQuestion(question.id);
                                        }}
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                      >
                                        Delete
                                      </motion.button>
                                    </div>
                                  </>
                                )}
                                {errorUpdatingField &&
                                  editingQuestionId === question.id && (
                                    <p className="text-red-500 mt-2">
                                      {errorUpdatingField?.message ||
                                        "Update failed."}
                                    </p>
                                  )}
                              </motion.div>
                            )}
                          </motion.div>
                        ))}
                    </AnimatePresence>

                    {errorCustomFields && (
                      <p className="text-red-500 mt-4">
                        {errorCustomFields?.message ||
                          "Failed to load questions."}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </main>
        <Footer />
      </div>
    </Helmet>
  );
};

export default CustomFields;
